import moment from 'moment';

export const FREQUENCY_OPTIONS = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'quarterly', label: 'Quarterly' },
];

export const getDayOptions = (frequency) => {
    switch (frequency) {
        case 'monthly':
            return [
                { value: '1st', label: '1st of month' },
                { value: 'first_business_day', label: '1st business day' },
                { value: '15th', label: '15th of month' },
                { value: 'last_day', label: 'Last day of month' },
            ];
        case 'weekly':
            return [
                { value: 'monday', label: 'Monday' },
                { value: 'tuesday', label: 'Tuesday' },
                { value: 'wednesday', label: 'Wednesday' },
                { value: 'thursday', label: 'Thursday' },
                { value: 'friday', label: 'Friday' },
            ];
        case 'quarterly':
            return [
                { value: '1st', label: '1st of quarter' },
                { value: 'first_business_day', label: '1st business day' },
            ];
        default:
            return [];
    }
};

export const TIME_OPTIONS = [
    { value: '06:00', label: '6:00 AM' },
    { value: '07:00', label: '7:00 AM' },
    { value: '08:00', label: '8:00 AM' },
    { value: '09:00', label: '9:00 AM' },
    { value: '10:00', label: '10:00 AM' },
    { value: '11:00', label: '11:00 AM' },
    { value: '12:00', label: '12:00 PM' },
    { value: '13:00', label: '1:00 PM' },
    { value: '14:00', label: '2:00 PM' },
    { value: '15:00', label: '3:00 PM' },
    { value: '16:00', label: '4:00 PM' },
    { value: '17:00', label: '5:00 PM' },
    { value: '18:00', label: '6:00 PM' },
    { value: '19:00', label: '7:00 PM' },
    { value: '20:00', label: '8:00 PM' },
];

export const REPORT_PERIOD_OPTIONS = [
    { value: 'previous_month', label: 'Previous calendar month' },
    { value: 'previous_week', label: 'Previous week (Mon-Sun)' },
    { value: 'previous_quarter', label: 'Previous quarter' },
];

const computeMonthlyNextRun = (base, day) => {
    if (day === '1st') {
        return base.date() > 1
            ? base.clone().add(1, 'month').date(1)
            : base.clone().date(1);
    }

    if (day === '15th') {
        return base.date() > 15
            ? base.clone().add(1, 'month').date(15)
            : base.clone().date(15);
    }

    if (day === 'last_day') {
        return base.clone().endOf('month');
    }

    if (day === 'first_business_day') {
        let candidate = base.clone().date(1);

        while (candidate.day() === 0 || candidate.day() === 6) {
            candidate.add(1, 'day');
        }

        if (candidate.isBefore(base, 'day')) {
            candidate = base.clone().add(1, 'month').date(1);

            while (candidate.day() === 0 || candidate.day() === 6) {
                candidate.add(1, 'day');
            }
        }

        return candidate;
    }

    return base.clone();
};

const computeWeeklyNextRun = (base, day) => {
    const dayMap = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5 };
    const targetIsoDay = dayMap[day] || 1;
    const candidate = base.clone().isoWeekday(targetIsoDay);

    if (candidate.isBefore(base, 'day')) {
        candidate.add(1, 'week');
    }

    return candidate;
};

const computeQuarterlyNextRun = (base, day) => {
    const currentQuarter = base.quarter();
    const candidate = base.clone().quarter(currentQuarter + 1).startOf('quarter');

    if (day === 'first_business_day') {
        while (candidate.day() === 0 || candidate.day() === 6) {
            candidate.add(1, 'day');
        }
    }

    return candidate;
};

export const computeNextRun = (frequency, day, baseDate) => {
    const base = moment.isMoment(baseDate) ? baseDate.clone() : moment(baseDate);

    if (frequency === 'monthly') {
        return computeMonthlyNextRun(base, day);
    }

    if (frequency === 'weekly') {
        return computeWeeklyNextRun(base, day);
    }

    if (frequency === 'quarterly') {
        return computeQuarterlyNextRun(base, day);
    }

    return base.clone();
};

export const getPeriodText = (reportPeriod, nextRun) => {
    if (reportPeriod === 'previous_month') {
        const prevMonth = nextRun.clone().subtract(1, 'month');
        const startP = prevMonth.clone().startOf('month').format('MMM D');
        const endP = prevMonth.clone().endOf('month').format('MMM D, YYYY');

        return `${startP} - ${endP}`;
    }

    if (reportPeriod === 'previous_week') {
        const prevWeek = nextRun.clone().subtract(1, 'week');
        const startP = prevWeek.clone().startOf('isoWeek').format('MMM D');
        const endP = prevWeek.clone().endOf('isoWeek').format('MMM D, YYYY');

        return `${startP} - ${endP}`;
    }

    if (reportPeriod === 'previous_quarter') {
        const prevQuarter = nextRun.clone().subtract(1, 'quarter');
        const startP = prevQuarter.clone().startOf('quarter').format('MMM D');
        const endP = prevQuarter.clone().endOf('quarter').format('MMM D, YYYY');

        return `${startP} - ${endP}`;
    }

    return 'the requested period';
};

export const computeDynamicPreview = ({ frequency, day, timeOfDay, reportPeriod, startsOn }) => {
    const timeOption = TIME_OPTIONS.find((t) => t.value === timeOfDay);
    const timeLabel = timeOption ? timeOption.label : timeOfDay;

    const baseDate = startsOn && moment(startsOn).isValid() ? moment(startsOn) : moment();
    const nextRun = computeNextRun(frequency, day, baseDate);
    const nextRunFormatted = nextRun.format('MMM D, YYYY');
    const periodText = getPeriodText(reportPeriod, nextRun);

    return `Next run: ${nextRunFormatted} at ${timeLabel} ET - will cover ${periodText}.`;
};

export const formatFrequencyDay = (frequency, day) => {
    if (frequency === 'monthly') {
        const map = {
            '1st': '1st',
            first_business_day: '1st business day',
            '15th': '15th',
            last_day: 'Last day',
        };

        return map[day] || day || '1st';
    }

    if (frequency === 'weekly') {
        const map = {
            monday: 'Monday',
            tuesday: 'Tuesday',
            wednesday: 'Wednesday',
            thursday: 'Thursday',
            friday: 'Friday',
        };

        return map[day] || day || 'Monday';
    }

    if (frequency === 'quarterly') {
        const map = {
            '1st': '1st',
            first_business_day: '1st business day',
        };

        return map[day] || day || '1st';
    }

    return day;
};

export const formatTimeOfDay = (timeOfDay) => {
    if (!timeOfDay) return '8:00 AM';

    const m = moment(timeOfDay, 'HH:mm');

    return m.isValid() ? m.format('h:mm A') : timeOfDay;
};

export const formatFrequency = (schedule) => {
    if (!schedule) return '';
    if (schedule.frequency_label) return schedule.frequency_label;

    const freqCapitalized = schedule.frequency
        ? schedule.frequency.charAt(0).toUpperCase() + schedule.frequency.slice(1)
        : 'Monthly';
    const dayLabel = formatFrequencyDay(schedule.frequency, schedule.frequency_day);
    const timeLabel = formatTimeOfDay(schedule.time_of_day);

    return `${freqCapitalized} - ${dayLabel} - ${timeLabel} ET`;
};

export const formatReportPeriod = (period) => {
    const map = {
        previous_month: 'Previous calendar month',
        previous_week: 'Previous week (Mon-Sun)',
        previous_quarter: 'Previous quarter',
    };

    return map[period] || period || 'Previous calendar month';
};

export const formatCoveragePeriod = (nextRunAt, reportPeriod) => {
    if (!nextRunAt) return '';

    const runMoment = moment(nextRunAt);

    if (!runMoment.isValid()) return '';

    if (reportPeriod === 'previous_month') {
        const prevMonth = runMoment.clone().subtract(1, 'month');

        return `${prevMonth.clone().startOf('month').format('MMM D')} - ${prevMonth.clone().endOf('month').format('D')}`;
    }

    if (reportPeriod === 'previous_week') {
        const prevWeek = runMoment.clone().subtract(1, 'week');

        return `${prevWeek.clone().startOf('isoWeek').format('MMM D')} - ${prevWeek.clone().endOf('isoWeek').format('MMM D')}`;
    }

    if (reportPeriod === 'previous_quarter') {
        const prevQuarter = runMoment.clone().subtract(1, 'quarter');

        return `${prevQuarter.clone().startOf('quarter').format('MMM D')} - ${prevQuarter.clone().endOf('quarter').format('MMM D')}`;
    }

    return '';
};

export const deduplicateByEmail = (list) => {
    const seen = new Set();

    return (list || []).filter((item) => {
        const rawEmail = typeof item === 'string' ? item : item?.email;
        const email = (rawEmail || '').trim().toLowerCase();

        if (!email || seen.has(email)) return false;

        seen.add(email);
        return true;
    });
};

export const normalizeRecipientsForPayload = (recipients) => {
    if (!Array.isArray(recipients)) return [];

    const expanded = [];
    recipients.forEach((r) => {
        if (typeof r === 'string' && (r.includes(',') || r.includes(';'))) {
            r.split(/[,;]/).forEach((sub) => {
                if (sub.trim()) expanded.push(sub.trim());
            });
        } else {
            expanded.push(r);
        }
    });

    const formattedRecipients = expanded.map((r) => {
        if (typeof r === 'string') {
            const match = r.match(/<([^>]+)>/);
            const email = match ? match[1] : r;

            return { email: email.trim() };
        }

        return {
            id: r.id,
            email: (r.email || '').trim(),
            name: `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.name || r.email,
        };
    });

    const uniqueRecipients = [];
    const seenEmails = new Set();

    formattedRecipients.forEach((item) => {
        const emailKey = (item.email || '').toLowerCase();

        if (emailKey && !seenEmails.has(emailKey)) {
            seenEmails.add(emailKey);
            uniqueRecipients.push(item);
        }
    });

    return uniqueRecipients;
};

